/**
 * Custom Zero mutators. Run client-side for optimistic updates and
 * server-side (via PushProcessor in apps/api/src/routes/zero-push.ts)
 * for authoritative writes.
 *
 * Conventions:
 *  - All mutators are async functions on `(tx, input)`.
 *  - First check auth via `authData` captured by the closure. Throw to
 *    reject the mutation; the server propagates the error to the client.
 *  - Use `tx.mutate.<table>.insert/update/delete` for the actual writes.
 *    Same code path runs on the client (against the local replica) and
 *    on the server (against Postgres via Drizzle).
 */
import type { CustomMutatorDefs } from "@rocicorp/zero";

export type AuthData = { sub: string }; // sub == users.id (matches BA JWT)

export type Mutators = ReturnType<typeof createMutators>;

export function createMutators(authData: AuthData | undefined) {
  const meId = () => {
    if (!authData?.sub) throw new Error("unauthorized");
    return authData.sub;
  };

  return {
    collections: {
      create: async (
        tx,
        input: {
          id: string;
          name: string;
          themeColor?: string | null;
          icon?: string | null;
        },
      ) => {
        const me = meId();
        const now = Date.now();
        await tx.mutate.collections!.insert({
          id: input.id,
          owner_user_id: me,
          name: input.name,
          icon: input.icon ?? null,
          theme_color: input.themeColor ?? null,
          cover_media_id: null,
          folder_id: null,
          archived_at: null,
          created_at: now,
          updated_at: now,
          deleted_at: null,
        });
      },
    },

    entries: {
      // Create a photo entry tied to a single media id and attach to a
      // collection. Three atomic writes: entries, entry_media, collection_entries.
      createPhoto: async (
        tx,
        input: {
          id: string;
          collectionId: string;
          mediaId: string;
          title?: string | null;
        },
      ) => {
        const me = meId();
        const now = Date.now();
        await tx.mutate.entries!.insert({
          id: input.id,
          kind: "photo",
          author_user_id: me,
          title: input.title ?? null,
          body: null,
          occurred_at: null,
          occurred_end_at: null,
          place_name: null,
          payload: {},
          created_at: now,
          updated_at: now,
          deleted_at: null,
        });
        await tx.mutate.entry_media!.insert({
          entry_id: input.id,
          media_id: input.mediaId,
          position: 0,
          created_at: now,
        });
        await tx.mutate.collection_entries!.insert({
          collection_id: input.collectionId,
          entry_id: input.id,
          added_by: me,
          added_at: now,
        });
      },
    },
  } as const satisfies CustomMutatorDefs;
}
